// Load-time repair for the company "messaging" sections, mirroring the
// server-side rules in backend/libs/companyMessagingShape.js.
//
// WHY: in the production build mobx-state-tree skips type checks, and when
// any ONE item in a section fails the model (a '' where a number belongs, a
// null question, an offering missing a text field) the whole section is
// silently replaced by its empty default. The page then shows "No offerings
// yet" while the data is still in the database, and the next save writes
// that empty section over it. Repairing each item here means a stray value
// costs at most that one field, never the section.

export const MESSAGING_FIELDS = [
  'background',
  'differentiators',
  'addressableProblems',
  'generalAsks',
  'offerings',
  'questionsAndAnswers',
  'topCompetitors',
  'prospectProfile',
];

// Flat per-section counter the backend bumps on every write of that section.
export const versionField = (field) => `${field}Version`;

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const str = (v, fallback) => (typeof v === 'string' ? v : fallback);
const num = (v) => {
  if (v === undefined || v === null || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};
const bool = (v, fallback) => (typeof v === 'boolean' ? v : fallback);

const stringArray = (v) => (Array.isArray(v) ? v.filter((item) => typeof item === 'string') : []);

const withFields = (item, { strings = {}, numbers = [], booleans = {} }) => {
  const out = { ...item };
  Object.entries(strings).forEach(([field, required]) => {
    const v = str(item[field], required ? '' : undefined);
    if (v === undefined) delete out[field]; else out[field] = v;
  });
  numbers.forEach((field) => {
    const v = num(item[field]);
    if (v === undefined) delete out[field]; else out[field] = v;
  });
  Object.entries(booleans).forEach(([field, fallback]) => {
    out[field] = bool(item[field], fallback);
  });
  return out;
};

const objectArray = (v, schema) => (Array.isArray(v) ? v.filter(isPlainObject).map((item) => withFields(item, schema)) : []);

const BACKGROUND = {
  strings: { ceo: true, history: true, mission: true, uploadKey: false, uploadLink: false },
  numbers: ['totalRevenue', 'yearFounded', 'numberOfEmployees', 'numberOfOffices'],
  booleans: { isPublic: false },
};
const OFFERING = {
  strings: { offering: true, differentiator: true, asks: true, qualifyingQuestions: true, typicalObjections: true, uploadKey: false, uploadLink: false },
};
const QUESTION_ANSWER = {
  strings: { question: true, answer: true, related_to: true, uploadKey: false },
};
const COMPETITOR = {
  strings: { companyName: false, website: false, strengths: false, weaknesses: false, location: false },
  numbers: ['revenue'],
};

// Returns the messaging part of a company snapshot with every item repaired.
// prospectProfile keeps its own preProcessSnapshot; here it is only reduced to
// plain objects so a stray null or string in the list cannot sink the section.
export const sanitizeCompanyMessaging = (snapshot = {}) => {
  const out = {
    differentiators: stringArray(snapshot.differentiators),
    generalAsks: stringArray(snapshot.generalAsks),
    addressableProblems: stringArray(snapshot.addressableProblems),
    offerings: objectArray(snapshot.offerings, OFFERING),
    questionsAndAnswers: objectArray(snapshot.questionsAndAnswers, QUESTION_ANSWER),
    topCompetitors: objectArray(snapshot.topCompetitors, COMPETITOR),
    prospectProfile: Array.isArray(snapshot.prospectProfile) ? snapshot.prospectProfile.filter(isPlainObject) : [],
  };
  if (isPlainObject(snapshot.background)) {
    out.background = withFields(snapshot.background, BACKGROUND);
  } else if (snapshot.background !== undefined) {
    // A non-object background: let the model's default stand in.
    out.background = undefined;
  }
  MESSAGING_FIELDS.forEach((field) => {
    const v = num(snapshot[versionField(field)]);
    out[versionField(field)] = v === undefined ? undefined : v;
  });
  return out;
};
